import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import ApplyOnlineLayout from '../components/ApplyOnlineLayout/ApplyOnlineLayout';
import { Loader } from '@sunbit-dev/web-ui-lib';
import Origin from '@sunbit-dev/common/enums/Origin';
import LandingPage from './LandingPageContainer';
import { AppContext } from '../contexts/appContext';
import { PurchaseActionContext, PurchaseContext } from '../contexts/purchaseContext';
import { UserActionContext } from '../contexts/userContext';
import { getDepartmentIdHeader } from '../helpers/urlHelper';
import { FeatureFlags } from '../constants/featureFlags';
import { History, Location } from 'history';
import SunbitApi from '../services/SunbitApi';
import { Pages } from '../constants/wizardSteps';
import { ABTestsContext } from '../contexts/abTests/abTestsContext';
import { AbTests } from '../constants/abTests';
import { useQueryParams } from '~/hooks/useQueryParams';
import { CustomerVerification } from '../components/CustomerVerification/CustomerVerification';

interface IMainLayoutContainerProps {
  history: History;
  location: Location;
  retailerId: string;
}

interface GetRetailerDataError {
  httpStatus?: number;
  code?: string;
  message?: string;
}

interface QsStringData {
  [key: string]: string;
}

const MainLayoutContainer: FunctionComponent<IMainLayoutContainerProps> = (props) => {
  const appContext = useContext(AppContext);

  const { setIsPs1Associated } = appContext;
  const purchaseContext = useContext(PurchaseContext);
  const purchaseActionContext = useContext(PurchaseActionContext);
  const userActionContext = useContext(UserActionContext);
  const abTestsContext = useContext(ABTestsContext);
  const [isOnlineApplicationLoaded, setIsOnlineApplicationLoaded] = useState(false); // loader is up when initial (false)

  const { children, location, history, retailerId } = props;

  const historyManager = useCallback(() => {
    const url = history.location.pathname + (history.location.search || '');
    history.push(url, undefined);
  }, [history]);

  const handleSkipLandingPage = useCallback(
    (
      { skipLandingPageParticipant: _skipLandingPageParticipant, skipLandingPage: _skipLandingPage }: QsStringData,
      { isUserVerified }: { isUserVerified: boolean }
    ) => {
      const skipLandingPageParticipant = _skipLandingPageParticipant === 'true';
      const skipLandingPage = _skipLandingPage === 'true';
      const isActive = skipLandingPageParticipant && skipLandingPage;
      const isParticipant = skipLandingPageParticipant;

      abTestsContext.data.set({ testName: AbTests.SkipLandingPage, data: { isActive, isParticipant } });
      abTestsContext.properties.set({ testName: AbTests.SkipLandingPage, properties: { skipLandingPageParticipant, skipLandingPage } });

      if (isActive && !isUserVerified) appContext.setIsActiveApplication(true);
      historyManager();
    },
    [abTestsContext.data, abTestsContext.properties, appContext, historyManager]
  );

  const getRetailerData = useCallback(async () => {
    try {
      await appContext.getRetailerData();
      setIsOnlineApplicationLoaded(true);
    } catch (e) {
      const err = e as GetRetailerDataError;
      setIsOnlineApplicationLoaded(false);

      if (window.sunbitEnv === 'dev') {
        console.error(`Retailer id ${retailerId} is not existing`);
        return;
      }

      let location = 'https://sunbit.com';
      if (err.httpStatus === 422 && err.code === 'linkExpired' && err.message) {
        location = `/${err.message}`;
      }
      window.location.replace(location);
    }
  }, [appContext, retailerId]);

  const onGetStarted = useCallback(() => {
    appContext.setIsActiveApplication(true);
    datadogRum.addAction(`page-view: enter-phone-number-land-in-page `, {});
    historyManager();
  }, [appContext, historyManager]);

  const returnToCartAction = useCallback(async () => {
    if (purchaseContext.offerId) {
      await SunbitApi.abortApplication(purchaseContext.offerId);
    }
    appContext.resetAppData();
    purchaseActionContext.reset();
    userActionContext.reset();

    const departmentHeader = getDepartmentIdHeader(history.location.search);

    window.history.replaceState({}, document.title, `${history.location.pathname}${departmentHeader}`);
    history.replace({ ...history.location, search: departmentHeader });
  }, [purchaseContext.offerId, appContext, purchaseActionContext, userActionContext, history]);

  const userAlreadyVerifyHandler = useCallback(
    async (token: string, type: string = '') => {
      try {
        SunbitApi.setStoreToken(token);
        let userLogin;
        if (type !== 'm') {
          userLogin = await SunbitApi.loginVerified();
        } else {
          userLogin = await SunbitApi.loginMerged();
        }
        onGetStarted();
        userActionContext.setCustomer({ ...userLogin.customer });
        appContext.setNextPage(Pages.EmailVerification);
        await getRetailerData();
      } catch (e) {
        window.location.replace(location.pathname);
      } finally {
        setIsOnlineApplicationLoaded(true);
      }
    },
    [getRetailerData, onGetStarted, userActionContext, appContext, location]
  );

  useEffect(() => {
    window.onpopstate = (e: PopStateEvent) => {
      returnToCartAction();
      e.preventDefault();
    };

    const { token, tv, t, skipLandingPageParticipant, skipLandingPage, ps1, customerVerified } = useQueryParams();

    datadogRum.addRumGlobalContext('applicationContext', { isPs1Associated: +ps1 || 0 });

    // User referred from ps1
    ps1 && setIsPs1Associated(+ps1);

    const isUserVerified = +(tv || 0) === 1 && !!token;

    if (isUserVerified) userAlreadyVerifyHandler(token as string, t as string);
    else {
      // In case this is a returning customer, that verified and don't have urlParams of an existing customer;
      if (customerVerified) {
        onGetStarted();
      }
      getRetailerData();
    }

    handleSkipLandingPage({ skipLandingPageParticipant, skipLandingPage } as QsStringData, { isUserVerified });

    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const isDiscountTire = useMemo(() => {
    const DT_FEATURE_FLAG = FeatureFlags.DT_SERVICES_FLAG;
    const chainFeatures = appContext.retailer.chainFeatures || {};
    return chainFeatures[DT_FEATURE_FLAG] || false;
  }, [appContext.retailer.chainFeatures]);

  if (!isOnlineApplicationLoaded) {
    return <Loader active />;
  }

  const {
    merchantAprLowerLimit,
    merchantAprUpperLimit,
    applicationAmountFinancedLowerLimit,
    applicationAmountLowerLimit,
    applicationAmountUpperLimit,
    merchantName,
    merchantOriginatorActiveStates,
    merchantOriginator,
    merchantVertical,
    merchantAddress,
    onlineLogoUrl,
    dpUpdate,
  } = appContext.retailer || {};

  if (appContext.appData.isExtraVerificationNeeded) {
    return <CustomerVerification />;
  }

  if (!appContext.appData.isActiveApplication) {
    return (
      <LandingPage
        {...{
          submit: onGetStarted,
          retailerName: merchantName,
          merchantAprLowerLimit,
          merchantAprUpperLimit,
          merchantOriginatorActiveStates,
          applicationAmountFinancedLowerLimit,
          verticalGroup: merchantVertical,
          merchantOriginator,
          merchantVertical,
          isDiscountTire,
          state: merchantAddress.state,
          logoUrl: onlineLogoUrl,
          dpUpdate,
        }}
      />
    );
  }

  return (
    <ApplyOnlineLayout
      {...{
        aprLowerLimit: merchantAprLowerLimit,
        aprUpperLimit: merchantAprUpperLimit,
        amountFinancedLowerLimit: applicationAmountFinancedLowerLimit,
        amountFinancedUpperLimit: 3000,
        applicationAmountLowerLimit,
        applicationAmountUpperLimit,
        retailerName: merchantName,
        activeStates: merchantOriginatorActiveStates,
        origin: Origin.ONLINE_APPLICATION,
        returnToCartAction,
        page: appContext.appData.page,
        currentPageName: appContext.appData.pageName,
        children,
      }}
      showHeaderTitle
    />
  );
};

export default MainLayoutContainer;
